'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { aiModelsApi } from '@/lib/ai-models-api';
import { usePrivateMode } from '@/lib/privateMode';
import { SCRATCHPAD_MAX, ScratchItem, applyScratch, copyWithScratch, useScratchpad } from '@/lib/scratchpad';
import { scratchKindLabel, scratchPreview } from '@/lib/scratchpadItems';
import { useToast } from './Toast';

/** "just now" / "4m" / "2h" — enough to tell one copy from another. */
function timeAgo(ts: number): string {
  const secs = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** Base64 from the image service may or may not already carry the data: prefix. */
function asDataUrl(image: string): string {
  return image.startsWith('data:') ? image : `data:image/png;base64,${image}`;
}

/**
 * Squash a generated preview down to a real thumbnail before it is stored.
 * sessionStorage is a few MB and holds ten of these, so keeping the full 256px
 * PNG would be the one thing able to blow the quota.
 */
function shrinkToThumbnail(dataUrl: string, size = 96): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('no 2d context'));
        return;
      }
      const side = Math.min(img.width, img.height);
      ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, size, size);
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    img.onerror = () => reject(new Error('preview failed to decode'));
    img.src = dataUrl;
  });
}

function KindIcon({ kind }: { kind: ScratchItem['kind'] }) {
  if (kind === 'settings') {
    return (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h7.5M8.25 12h7.5m-7.5 5.25h4.5M6.75 3h10.5A2.25 2.25 0 0119.5 5.25v13.5A2.25 2.25 0 0117.25 21H6.75A2.25 2.25 0 014.5 18.75V5.25A2.25 2.25 0 016.75 3z" />
    </svg>
  );
}

function ScratchRow({ item, onApplied }: { item: ScratchItem; onApplied: () => void }) {
  const toast = useToast();
  const remove = useScratchpad((s) => s.remove);
  const setThumbnail = useScratchpad((s) => s.setThumbnail);
  const canApply = useScratchpad((s) => s.canApply);
  const [generating, setGenerating] = useState(false);

  const handleApply = () => {
    if (applyScratch(item.text)) {
      toast.success('Applied to prompt');
      onApplied();
    } else {
      toast.error('No prompt field on this page');
    }
  };

  const handleCopy = async () => {
    const ok = await copyWithScratch(item.text, {
      kind: item.kind,
      thumbnail: item.thumbnail,
      source: item.source,
    });
    if (ok) toast.success('Copied');
    else toast.error('Clipboard unavailable');
  };

  /** Text-only rows can earn a thumbnail on demand — never automatically, it costs GPU. */
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await aiModelsApi.imagegenGenerate({
        prompt: item.text,
        width: 256,
        height: 256,
        num_inference_steps: 4,
        model: 'z-image-turbo',
      });
      setThumbnail(item.id, await shrinkToThumbnail(asDataUrl(result.image)));
    } catch {
      toast.error('Could not generate a preview');
    } finally {
      setGenerating(false);
    }
  };

  const thumbSrc = item.thumbnail
    ? item.thumbnail.startsWith('data:')
      ? item.thumbnail
      : api.getThumbnailUrl(item.thumbnail)
    : null;

  return (
    <li className="group flex gap-3 px-3 py-2.5 rounded-lg hover:bg-[var(--bg-hover)] transition-colors">
      <div className="shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-[var(--bg-primary)] flex items-center justify-center text-[var(--text-muted)]">
        {thumbSrc ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={thumbSrc} alt="" className="w-full h-full object-cover" />
        ) : generating ? (
          <svg className="w-4 h-4 animate-spin text-[var(--accent)]" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <KindIcon kind={item.kind} />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
          <span className="font-medium text-[var(--accent)]">{scratchKindLabel(item.kind)}</span>
          <span>·</span>
          <span>{timeAgo(item.createdAt)}</span>
        </div>
        <p className="text-sm text-[var(--text-primary)] break-words line-clamp-2" title={item.text}>
          {scratchPreview(item.text)}
        </p>
        {item.source && (
          <p className="text-xs text-[var(--text-muted)] mt-0.5 truncate" title={item.source}>
            {item.source}
          </p>
        )}
      </div>

      <div className="shrink-0 flex items-start gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        {canApply && (
          <button
            onClick={handleApply}
            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-black hover:bg-[var(--accent)] cursor-pointer"
            title="Apply to the prompt field"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z" />
            </svg>
          </button>
        )}
        <button
          onClick={handleCopy}
          className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] cursor-pointer"
          title="Copy again"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
          </svg>
        </button>
        {!item.thumbnail && (
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-elevated)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            title="Generate a thumbnail from this text"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
            </svg>
          </button>
        )}
        <button
          onClick={() => remove(item.id)}
          className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--error)] hover:bg-[var(--bg-elevated)] cursor-pointer"
          title="Remove"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </li>
  );
}

/**
 * Header nav entry for the private-mode scratchpad. Renders nothing at all unless
 * private mode is unlocked, so the feature — and its contents — disappear with it.
 */
export default function ScratchpadPanel() {
  const privateEnabled = usePrivateMode((s) => s.enabled);
  const items = useScratchpad((s) => s.items);
  const open = useScratchpad((s) => s.open);
  const setOpen = useScratchpad((s) => s.setOpen);
  const clear = useScratchpad((s) => s.clear);

  const [mounted, setMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // sessionStorage is client-only; reflect it after mount to avoid a hydration mismatch.
  useEffect(() => setMounted(true), []);

  const close = useCallback(() => setOpen(false), [setOpen]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  if (!mounted || !privateEnabled) return null;

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium cursor-pointer ${
          open
            ? 'bg-[var(--accent-soft)] text-[var(--accent)]'
            : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
        }`}
        title="Scratchpad — the last things you copied"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
        </svg>
        Scratchpad
        {items.length > 0 && (
          <span className="min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-[var(--accent)] text-black text-[10px] leading-none font-semibold flex items-center justify-center">
            {items.length}
          </span>
        )}
      </button>

      {/* Anchored right: the button sits at the end of the nav, so a left-anchored
          panel would run off the viewport. Opening leftward keeps it on screen. */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-[26rem] max-w-[calc(100vw-2rem)] bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--border-subtle)]">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Scratchpad</h3>
              <span className="text-xs text-[var(--text-muted)]">
                {items.length}/{SCRATCHPAD_MAX}
              </span>
            </div>
            {items.length > 0 && (
              <button
                onClick={clear}
                className="text-xs text-[var(--text-muted)] hover:text-[var(--error)] transition-colors cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-[var(--text-muted)]">Nothing copied yet</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                &quot;Use Settings&quot; and copied prompts or text land here.
              </p>
            </div>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto p-1.5 space-y-0.5">
              {items.map((item) => (
                <ScratchRow key={item.id} item={item} onApplied={close} />
              ))}
            </ul>
          )}

          <div className="px-4 py-2 border-t border-[var(--border-subtle)] bg-[var(--bg-primary)]">
            <p className="text-[11px] text-[var(--text-muted)]">
              Held for this tab only, and cleared when private mode is turned off.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
